import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Plus, Trash2, GitBranch, Archive, PlayCircle } from "lucide-react";

export default function Formulations() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["formulations-all"],
    queryFn: async () => (await api.get("/formulations")).data,
  });
  const [editing, setEditing] = useState(null); // formulation object or null
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="formulations-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="data-label">Sensitive IP</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Formulations</h1>
        </div>
        <button onClick={() => setCreating(true)}
                className="btn-primary" data-testid="formulation-new">
          <Plus className="w-4 h-4 mr-1" /> New
        </button>
      </div>

      {(creating || editing) && (
        <FormulationEditor
          initial={editing}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); qc.invalidateQueries({ queryKey: ["formulations-all"] }); }}
        />
      )}

      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="formulations-list">
        {data.length === 0 && <div className="p-6 text-sm text-muted-foreground">No formulations yet.</div>}
        {data.map(f => (
          <div key={f.id} className="p-4 space-y-2" data-testid={`formulation-${f.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <FlaskConical className="w-4 h-4 text-secondary" />
                  <span className="font-semibold">{f.name}</span>
                  <span className="badge-flat bg-primary/10 text-primary">v{f.version}</span>
                  <span className={`badge-flat ${f.status === "active" ? "bg-success/10 text-success" : f.status === "draft" ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>
                    {f.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Yields {f.batch_yield_qty} per batch · {f.ingredients.length} ingredients
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {f.status !== "archived" && (
                  <button onClick={() => setEditing(f)} className="btn-ghost text-xs"
                          data-testid={`formulation-edit-${f.id}`}>Edit</button>
                )}
                {f.status === "draft" && (
                  <button onClick={() => activate(f.id, qc)} className="btn-primary text-xs"
                          data-testid={`formulation-activate-${f.id}`}>
                    <PlayCircle className="w-3 h-3 mr-1" /> Activate
                  </button>
                )}
                {f.status !== "archived" && (
                  <button onClick={() => archive(f.id, qc)} className="btn-ghost text-xs"
                          data-testid={`formulation-archive-${f.id}`}>
                    <Archive className="w-3 h-3 mr-1" /> Archive
                  </button>
                )}
              </div>
            </div>
            <ul className="pl-4 border-l-2 border-border text-sm space-y-1">
              {f.ingredients.sort((a,b) => a.sequence - b.sequence).map((ing, i) => (
                <IngredientRow key={i} ing={ing} />
              ))}
            </ul>
            {f.process_notes && (
              <div className="text-xs text-muted-foreground italic bg-muted/40 rounded-sm p-2">
                {f.process_notes}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

async function activate(id, qc) {
  try {
    await api.patch(`/formulations/${id}`, { status: "active" });
    toast.success("Activated");
    qc.invalidateQueries();
  } catch (e) { toast.error(formatError(e)); }
}
async function archive(id, qc) {
  if (!window.confirm("Archive this formulation?")) return;
  try {
    await api.patch(`/formulations/${id}`, { status: "archived" });
    toast.success("Archived");
    qc.invalidateQueries();
  } catch (e) { toast.error(formatError(e)); }
}

function IngredientRow({ ing }) {
  const { data: item } = useQuery({
    queryKey: ["item-lookup", ing.item_id],
    queryFn: async () => (await api.get(`/items`)).data.find(i => i.id === ing.item_id),
    staleTime: 60_000,
  });
  return (
    <li className="flex items-center gap-2">
      <span className="badge-flat bg-muted text-foreground w-6 justify-center">{ing.sequence}</span>
      <span className="font-medium">{item?.name || "…"}</span>
      <span className="font-mono text-xs text-muted-foreground">{item?.sku}</span>
      <span className="ml-auto font-mono font-semibold">{ing.percentage}%</span>
    </li>
  );
}

function FormulationEditor({ initial, onCancel, onSaved }) {
  const { data: items = [] } = useQuery({
    queryKey: ["items-mfg"],
    queryFn: async () => (await api.get("/items")).data.filter(i => i.category !== "packaging"),
  });
  const [name, setName] = useState(initial?.name || "");
  const [outputItemId, setOutputItemId] = useState(initial?.output_item_id || "");
  const [yieldQty, setYieldQty] = useState(String(initial?.batch_yield_qty ?? ""));
  const [processNotes, setProcessNotes] = useState(initial?.process_notes || "");
  const [ings, setIngs] = useState(initial?.ingredients || [{ item_id: "", percentage: "", sequence: 1, notes: "" }]);
  const [busy, setBusy] = useState(false);

  const sum = ings.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
  const valid = name && outputItemId && Number(yieldQty) > 0 && Math.abs(sum - 100) < 0.01 && ings.every(i => i.item_id && Number(i.percentage) > 0);

  const upd = (i, patch) => setIngs(L => L.map((x, ix) => ix === i ? { ...x, ...patch } : x));
  const add = () => setIngs(L => [...L, { item_id: "", percentage: "", sequence: L.length + 1, notes: "" }]);
  const rm = (i) => setIngs(L => L.filter((_, ix) => ix !== i));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name, output_item_id: outputItemId, batch_yield_qty: Number(yieldQty),
        process_notes: processNotes || null,
        ingredients: ings.map(i => ({
          item_id: i.item_id, percentage: Number(i.percentage),
          sequence: Number(i.sequence), notes: i.notes || null,
        })),
      };
      if (initial) await api.patch(`/formulations/${initial.id}`, body);
      else await api.post("/formulations", body);
      toast.success(initial ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-primary/40 rounded-sm p-4 space-y-4" data-testid="formulation-editor">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">
          {initial ? `Edit — ${initial.name} v${initial.version}` : "New formulation"}
        </h2>
        {initial && <span className="badge-flat bg-secondary/15 text-secondary">
          <GitBranch className="w-3 h-3 mr-1" /> Editing may bump version
        </span>}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="data-label">Name</span>
          <input className="field mt-1" value={name} onChange={(e) => setName(e.target.value)}
                 data-testid="formulation-name" />
        </label>
        <label className="block">
          <span className="data-label">Output item</span>
          <select className="field mt-1" value={outputItemId}
                  onChange={(e) => setOutputItemId(e.target.value)}
                  data-testid="formulation-output">
            <option value="">Choose…</option>
            {items.map(i => <option key={i.id} value={i.id}>{i.name} ({i.base_unit})</option>)}
          </select>
        </label>
        <label className="block">
          <span className="data-label">Batch yield qty (base unit)</span>
          <input type="number" step="0.001" className="field mt-1 font-mono"
                 value={yieldQty} onChange={(e) => setYieldQty(e.target.value)}
                 data-testid="formulation-yield" />
        </label>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="data-label">Ingredients — must sum to 100%</span>
          <span className={`font-mono text-sm font-semibold ${Math.abs(sum - 100) < 0.01 ? "text-success" : "text-destructive"}`}
                data-testid="formulation-sum">
            {sum.toFixed(2)}%
          </span>
        </div>
        <div className="space-y-2">
          {ings.map((ing, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`formulation-ing-${i}`}>
              <input type="number" min="1" className="field-sm col-span-1 text-center font-mono"
                     value={ing.sequence} onChange={(e) => upd(i, { sequence: e.target.value })}
                     data-testid={`formulation-ing-${i}-seq`} />
              <select className="field-sm col-span-5" value={ing.item_id}
                      onChange={(e) => upd(i, { item_id: e.target.value })}
                      data-testid={`formulation-ing-${i}-item`}>
                <option value="">Choose ingredient…</option>
                {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.base_unit})</option>)}
              </select>
              <input type="number" step="0.01" className="field-sm col-span-2 font-mono"
                     placeholder="%" value={ing.percentage}
                     onChange={(e) => upd(i, { percentage: e.target.value })}
                     data-testid={`formulation-ing-${i}-pct`} />
              <input className="field-sm col-span-3" placeholder="Notes"
                     value={ing.notes || ""}
                     onChange={(e) => upd(i, { notes: e.target.value })}
                     data-testid={`formulation-ing-${i}-notes`} />
              <button onClick={() => rm(i)} className="col-span-1 text-muted-foreground hover:text-destructive"
                      disabled={ings.length === 1}
                      data-testid={`formulation-ing-${i}-rm`}>
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          ))}
        </div>
        <button onClick={add} type="button"
                className="text-primary text-xs font-semibold uppercase tracking-widest flex items-center gap-1 mt-2"
                data-testid="formulation-ing-add">
          <Plus className="w-4 h-4" /> Add ingredient
        </button>
      </div>

      <label className="block">
        <span className="data-label">Process notes</span>
        <textarea className="field mt-1 min-h-[80px]" value={processNotes}
                  onChange={(e) => setProcessNotes(e.target.value)}
                  data-testid="formulation-process" />
      </label>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-ghost" data-testid="formulation-cancel">Cancel</button>
        <button onClick={save} disabled={busy || !valid} className="btn-primary"
                data-testid="formulation-save">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
